import { Injectable, signal, computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { signOut } from 'firebase/auth';

import { State, StorageState } from '../models/state.model';
import { FirebaseService } from './firebase.service';
import { environment } from '../../../environments/environment';
import { beginExpectedAppTeardown, endExpectedAppTeardown } from '../error-handler';

const initialState: State = {
  authUser: null,
  currentUser: null,
  currentAccount: null,
  currentOrganization: null,
  currentMember: null,
  currentRole: null,
  theme: 'system',
  language: 'en',
  sidebarOpen: true,
};

@Injectable({
  providedIn: 'root',
})
export class DataStoreService {
  private readonly firebase = inject(FirebaseService);
  private readonly router = inject(Router);

  private readonly _state = signal<State>({ ...initialState });

  /**
   * Temporary-chat toggle, shared (root-provided) across Home, the
   * chat page and the chat navbar. When `true`, the next new chat is minted with
   * a `local-` id and lives only in memory (no chat doc, no message writes, no
   * uploads). Kept OUTSIDE `State` (so `clearState()` on logout is unaffected);
   * it is reset explicitly by the chat page when a persisted chat is opened and
   * forced on for `temporary_enforced` users by Home.
   */
  private readonly _temporaryChatEnabled = signal(false);
  readonly temporaryChatEnabled = this._temporaryChatEnabled.asReadonly();

  /** Observable for subscription-based consumers */
  readonly state$ = toObservable(this._state);

  /** Computed read-only accessors */
  readonly authUser = computed(() => this._state().authUser);
  readonly currentUser = computed(() => this._state().currentUser);
  readonly currentAccount = computed(() => this._state().currentAccount);
  readonly currentOrganization = computed(() => this._state().currentOrganization);
  readonly currentMember = computed(() => this._state().currentMember);
  readonly currentRole = computed(() => this._state().currentRole);
  readonly theme = computed(() => this._state().theme);
  readonly language = computed(() => this._state().language);
  readonly sidebarOpen = computed(() => this._state().sidebarOpen);

  private authState: StorageState = {};
  private sessionState: StorageState = {};

  constructor() {
    if (!this.firebase.auth) {
      return;
    }

    let loggedIn = false;
    this.firebase.auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        if (authUser.isAnonymous || authUser.email === null) {
          this.clearState();
          this.clearAuthState();
          signOut(this.firebase.auth!);
          return;
        }

        this.updateState({ authUser });
        loggedIn = true;
      } else {
        // An involuntary sign-out (e.g. Firebase invalidating a dead refresh
        // token after the tab sat idle) clears `currentUser` synchronously
        // here, but the redirect below is async — components with effects
        // still watching the now-null org/user state (e.g. reloadOnOrgChange
        // in ChatHistoryComponent/FoldersComponent) can fire and throw before
        // the route change unmounts them. Mark this as expected teardown so
        // that race doesn't paint the crash overlay, same as the manual
        // sidebar sign-out.
        const wasLoggedIn = loggedIn;
        if (wasLoggedIn) {
          beginExpectedAppTeardown();
        }
        this.clearState();
        this.clearAuthState();
        if (wasLoggedIn) {
          loggedIn = false;
          this.router.navigate(['/login']).finally(() => endExpectedAppTeardown());
        }
      }
    });
  }

  /** Flip the shared temporary-chat toggle. */
  setTemporaryChatEnabled(on: boolean) {
    this._temporaryChatEnabled.set(on);
  }

  /**
   * Reset the temporary-chat toggle to its click-time default
   * (`enforced ? true : false`), which fires on EVERY New Chat affordance
   * click. Previously the toggle was only reset when a persisted chat
   * loaded or on Save Chat, so a user who turned it on/off and then clicked
   * "New Chat" again (while already on `/`, where
   * `onSameUrlNavigation:'ignore'` means Home's ngOnInit never re-runs) kept
   * the stale value. Called from each New-Chat entry point:
   * `ChatHistoryComponent.newChat()`, the `newChat` keyboard shortcut, and
   * the sidebar logo/wordmark link.
   */
  resetTemporaryChatDefault(): void {
    const user = this.currentUser();
    const enforced = user?.role !== 'admin' && !!user?.permissions?.chat?.temporary_enforced;
    this.setTemporaryChatEnabled(enforced);
  }

  getProperty<T extends keyof State>(property: T): State[T] {
    return this._state()[property];
  }

  updateState(newState: Partial<State>) {
    this._state.update((current) => ({ ...current, ...newState }));
  }

  clearState() {
    this._state.set({ ...initialState });
  }

  // -- Session storage --

  setSessionStateProperty(key: string, value: any) {
    this.sessionState[key] = true;
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  getSessionStateProperty(key: string) {
    return JSON.parse(sessionStorage.getItem(key) || 'null');
  }

  removeSessionStateProperty(key: string) {
    delete this.sessionState[key];
    sessionStorage.removeItem(key);
  }

  // -- Auth (localStorage) storage --

  setAuthStateProperty(key: string, value: any, stringify = true) {
    this.authState[key] = true;
    const data = stringify ? JSON.stringify(value) : value;
    localStorage.setItem(key, data);
  }

  getAuthStateProperty(key: string, parse = true) {
    const data = localStorage.getItem(key);
    return parse ? JSON.parse(data || 'null') : data;
  }

  removeAuthStateProperty(key: string) {
    delete this.authState[key];
    localStorage.removeItem(key);
  }

  clearAuthState() {
    for (const key in this.authState) {
      localStorage.removeItem(key);
    }
    localStorage.removeItem('currentAccount');
    this.authState = {};
  }
}
